import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetCatalogQuery, useGetRecordQuery, useMeQuery } from '@platform/state/api';
import { useLabel } from '@platform/hooks/useLabel';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { recordNumber } from '@platform/engine/views/recordNumber';
import { navGroup } from '@platform/navigation';
import { aiPageLabels } from './aiPageLabels';
import type { IAiPageContext } from './IAiPageContext';

/**
 * Контекст ШІ-асистента як у прототипі v4: «Простір › Модуль (або сторінка) › 3 · назва» (голий номер) — підзаголовок панелі й частина запиту
 * POST /ai/ask (сторінка, модуль, тип і запис). Вид сторінки (запис, модуль, інше) вибирає підказки порожньої розмови.
 */
export function useAiContext(ws: string): IAiPageContext {
  const { t } = useTranslation();
  const label = useLabel();
  const { pathname } = useLocation();
  const route = useWorkspaceParams();
  const module = route.module || undefined;
  const rt = route.rt || undefined;
  const id = route.id || undefined;
  const { data: me } = useMeQuery();
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws });
  const { data: record } = useGetRecordQuery({ ws, rt: rt ?? '', id: id ?? '' }, { skip: !ws || !rt || !id });
  const wsName = me?.workspaces.find((w) => w.id === ws)?.name ?? '';
  const moduleName = label(catalog?.modules.find((m) => m.key === module)?.name) || module || '';
  const [segment = '', sub = ''] = pathname.split('/').filter(Boolean);
  const pageKey = segment === 'members' && sub === 'invitations' ? 'homeNav.invitations' : navGroup(segment) ? aiPageLabels.area : aiPageLabels[segment];
  const page = module ? moduleName : pageKey ? t(pageKey) : segment;
  const recordText = id && record ? [recordNumber(record), record.title].filter(Boolean).join(' · ') : null;
  const path = [wsName, page, recordText].filter(Boolean).join(' › ');
  const kind = id ? 'record' : module ? 'module' : 'home';
  return {
    path, kind, moduleName,
    ask: { page: id ? 'record' : module ? 'module' : segment || 'home', module: module ?? null, recordType: rt ?? null, recordId: id ?? null, path },
  };
}
